import { useCallback, useEffect, useRef, useState } from "react";
import { saveBlobAsFile } from "../../../services/api/editor/export";
import {
  buildDownloadQualityOptions,
} from "../../../config/tools/downloadQualityTiers";
import {
  imageHasTransparency,
  probeImageLongestSide,
  runToolPrepDownloadPipeline,
} from "../lib/toolPrepDownloadPipeline";
export const TOOL_PREP_DOWNLOAD_THANK_YOU_INITIAL = {
  open: false,
  progress: 0,
  phase: "downloading",
  shareCopied: false,
  errorMessage: "",
  imageUrl: null,
  leadPhase: "downloading",
};

const OPTIONS_INITIAL = {
  open: false,
  previewUrl: null,
  sourceLongestSide: 0,
  hasTransparency: false,
  qualityOptions: [],
  highQualityAlert: "",
};

/**
 * Shared Download Options → Thank You flow for tool prep popups.
 *
 * variant: "editing" | "ai" | "passport"
 */
export function useToolPrepDownloadFlow({
  variant = "editing",
  sourceFileName = "",
  fallbackDownloadName = "image.png",
  getToken = null,
  toolKey = "",
  shareTitle = "LazyStudio",
  shareText = "I edited my photo with LazyStudio!",
  thankYouCopy = {},
  /** Optional: after digital export, e.g. passport print-sheet PDF */
  onAfterDigitalDownload = null,
}) {
  const [downloadBusy, setDownloadBusy] = useState(false);
  const [options, setOptions] = useState(OPTIONS_INITIAL);
  const [thankYou, setThankYou] = useState(TOOL_PREP_DOWNLOAD_THANK_YOU_INITIAL);
  const [shareFriendsOpen, setShareFriendsOpen] = useState(false);
  const progressTimerRef = useRef(null);
  const thankYouPreviewUrlRef = useRef(null);
  const sourceResolverRef = useRef(null);

  const clearProgressTimer = useCallback(() => {
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
  }, []);

  const revokeThankYouPreviewUrl = useCallback(() => {
    if (thankYouPreviewUrlRef.current) {
      URL.revokeObjectURL(thankYouPreviewUrlRef.current);
      thankYouPreviewUrlRef.current = null;
    }
  }, []);

  const closeOptions = useCallback(() => {
    setOptions(OPTIONS_INITIAL);
    sourceResolverRef.current = null;
  }, []);

  const handleThankYouDismiss = useCallback(() => {
    clearProgressTimer();
    revokeThankYouPreviewUrl();
    setShareFriendsOpen(false);
    setThankYou(TOOL_PREP_DOWNLOAD_THANK_YOU_INITIAL);
  }, [clearProgressTimer, revokeThankYouPreviewUrl]);

  const closeShareFriends = useCallback(() => {
    setShareFriendsOpen(false);
  }, []);

  const handleShare = useCallback(() => {
    setShareFriendsOpen(true);
  }, []);

  /**
   * Open Download Options.
   * @param {object} args
   * @param {string} args.previewUrl
   * @param {() => Promise<string>|string} [args.resolveImageUrl] - URL used for export (defaults to previewUrl)
   */
  const openDownloadOptions = useCallback(
    async ({ previewUrl, resolveImageUrl = null } = {}) => {
      if (!previewUrl) return;
      sourceResolverRef.current =
        typeof resolveImageUrl === "function"
          ? resolveImageUrl
          : async () => previewUrl;

      let sourceLongestSide = 0;
      let hasTransparency = false;
      let qualityOptions = [];
      let highQualityAlert = "";

      try {
        sourceLongestSide = await probeImageLongestSide(previewUrl);
        hasTransparency = await imageHasTransparency(previewUrl);
      } catch {
        sourceLongestSide = 0;
        hasTransparency = false;
      }

      if (variant === "editing") {
        qualityOptions = buildDownloadQualityOptions(sourceLongestSide);
        if (sourceLongestSide >= 2048) {
          highQualityAlert =
            "Your image is already high quality. Upscaling again will use credits.";
        }
      }

      setOptions({
        open: true,
        previewUrl,
        sourceLongestSide,
        hasTransparency,
        qualityOptions,
        highQualityAlert,
      });
    },
    [variant]
  );

  const startThankYouProgress = useCallback(
    (imageUrl, leadPhase = "downloading") => {
      clearProgressTimer();
      revokeThankYouPreviewUrl();
      setThankYou({
        open: true,
        progress: 6,
        phase: "downloading",
        shareCopied: false,
        errorMessage: "",
        imageUrl,
        leadPhase,
      });

      let progress = 6;
      progressTimerRef.current = setInterval(() => {
        progress = Math.min(progress + 2 + Math.random() * 3, 94);
        setThankYou((prev) =>
          prev.phase === "downloading"
            ? { ...prev, progress: Math.round(progress) }
            : prev
        );
      }, 120);
    },
    [clearProgressTimer, revokeThankYouPreviewUrl]
  );

  const confirmDownloadOptions = useCallback(
    async (selection) => {
      const previewForProgress = options.previewUrl;
      const resolve = sourceResolverRef.current;
      if (!resolve) return;

      closeOptions();
      setDownloadBusy(true);

      const initialPhase = selection?.needsUpscale ? "upscaling" : "downloading";
      startThankYouProgress(previewForProgress, initialPhase);

      const minDelay = new Promise((r) => setTimeout(r, 1400));

      try {
        const imageUrl = await resolve();
        if (!imageUrl) throw new Error("No image ready to download.");

        const showQuality = variant === "editing";
        const result = await runToolPrepDownloadPipeline({
          imageUrl,
          formatId: selection.formatId,
          qualityId: selection.qualityId,
          showQuality,
          getToken,
          onPhase: (phase) => {
            setThankYou((prev) =>
              prev.open ? { ...prev, leadPhase: phase } : prev
            );
          },
        });

        thankYouPreviewUrlRef.current = result.previewUrl;
        saveBlobAsFile(result.blob, selection.fileName);

        if (typeof onAfterDigitalDownload === "function") {
          await onAfterDigitalDownload({
            ...selection,
            imageUrl,
            previewUrl: result.previewUrl,
          });
        }

        await minDelay;
        clearProgressTimer();
        setThankYou((prev) => ({
          ...prev,
          progress: 100,
          imageUrl: result.previewUrl,
        }));
        await new Promise((r) => setTimeout(r, 420));
        setThankYou((prev) =>
          prev.open
            ? { ...prev, progress: 100, phase: "complete", leadPhase: "complete" }
            : prev
        );
      } catch (err) {
        await minDelay;
        clearProgressTimer();
        revokeThankYouPreviewUrl();
        setThankYou((prev) => ({
          ...prev,
          phase: "error",
          errorMessage: err?.message || "Could not download image.",
          leadPhase: "error",
        }));
      } finally {
        setDownloadBusy(false);
        sourceResolverRef.current = null;
      }
    },
    [
      clearProgressTimer,
      closeOptions,
      getToken,
      onAfterDigitalDownload,
      options.previewUrl,
      revokeThankYouPreviewUrl,
      startThankYouProgress,
      variant,
    ]
  );

  useEffect(
    () => () => {
      clearProgressTimer();
      revokeThankYouPreviewUrl();
    },
    [clearProgressTimer, revokeThankYouPreviewUrl]
  );

  const resolvedThankYouCopy = {
    ...thankYouCopy,
    ...(thankYou.leadPhase === "upscaling"
      ? {
          titleDownloading: "Improving quality…",
          leadDownloading: "Please wait — we’re making your image sharper before saving it.",
        }
      : {}),
  };

  return {
    downloadBusy,
    options,
    openDownloadOptions,
    closeOptions,
    confirmDownloadOptions,
    thankYou,
    thankYouCopy: resolvedThankYouCopy,
    handleThankYouDismiss,
    handleShare,
    shareFriendsOpen,
    closeShareFriends,
    toolKey,
    shareTitle,
    variant,
    sourceFileName,
    fallbackDownloadName,
  };
}
